import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  ListCardComponent,
  ListCardItem,
} from 'app/main/components/list-card/list-card.component';
import {
  LIST_PAGE_CONFIG,
  ListPageConfig,
  ListColumnConfig,
} from 'app/main/components/list-page-config/list-page.config';
import { AppointmentsService } from 'app/core/services/appointment/appointments.service';
import { Appointment } from 'app/core/models/appointment/appointment.model';
import { StatusUtils } from 'app/utils/status-utils';
import { PermissionService } from 'app/core/services/user/permission.service';
import { BaseListComponent } from 'app/shared/base/base-list.component';
import { ListStateStackService } from 'app/core/services/list/list-state-stack.service';
import { PageSizeSelectorComponent } from 'app/shared/components/page-size-selector/page-size-selector.component';

@Component({
  selector: 'app-appointment-list',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, ListCardComponent, PageSizeSelectorComponent],
  templateUrl: './appointment-list.component.html',
  styleUrl: './appointment-list.component.scss',
})
export class AppointmentListComponent extends BaseListComponent implements OnInit, OnDestroy, AfterViewInit {
  listConfig: ListPageConfig | null = LIST_PAGE_CONFIG['appointments'] ?? null;

  get columns(): ListColumnConfig[] {
    return this.listConfig?.columns ?? [];
  }

  appointments: Appointment[] = [];
  appointmentCards: ListCardItem[] = [];
  isLoading = false;

  override filterForm: {
    patientName: string;
    bsnNumber: string;
    date: string;
    sortBy?: string | null;
    sortDirection?: 'asc' | 'desc' | null;
  } = {
    patientName: '',
    bsnNumber: '',
    date: '',
    sortBy: null,
    sortDirection: null,
  };

  totalCount = 0;
  totalPage = 0;
  pageSizes = [10, 15, 25, 50, 100];

  activeTooltip: { text: string; x: number; y: number } | null = null;
  private tooltipTimeout: any = null;
  private pendingTooltipPosition: { x: number; y: number } | null = null;

  showTooltip(event: MouseEvent, text: string) {
    if (!text) return;
    const translatedText = this.translate.instant(text);

    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();

    const x = rect.left;
    const y = rect.bottom + 4;

    this.pendingTooltipPosition = { x, y };

    if (this.tooltipTimeout) clearTimeout(this.tooltipTimeout);
    this.tooltipTimeout = setTimeout(() => {
      if (this.pendingTooltipPosition) {
        this.activeTooltip = {
          text: translatedText,
          x: this.pendingTooltipPosition.x,
          y: this.pendingTooltipPosition.y,
        };
      }
    }, 300);
  }

  hideTooltip() {
    if (this.tooltipTimeout) {
      clearTimeout(this.tooltipTimeout);
      this.tooltipTimeout = null;
    }
    this.pendingTooltipPosition = null;
    this.activeTooltip = null;
  }

  constructor(
    private appointmentsService: AppointmentsService,
    private permissionService: PermissionService,
    private translate: TranslateService,
    listStateStack: ListStateStackService,
  ) {
    super(listStateStack, 'appointment-list');
    this.collapseElementId = 'collapseFilters'; // Set the collapse element ID for this list page
  }

  override ngOnInit(): void {
    super.ngOnInit();
    if (!this.stateWasRestored) {
      this.getAppointmentList(this.currentPage, this.pageSize);
    }
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  ngAfterViewInit(): void {
    this.restoreCollapseState();
    this.setupCollapseListener();
  }

  private setupCollapseListener(): void {
    if (!this.collapseElementId) return;
    const collapseElement = document.getElementById(this.collapseElementId);
    if (collapseElement) {
      collapseElement.addEventListener('show.bs.collapse', () => this.updateCollapseState(true));
      collapseElement.addEventListener('hide.bs.collapse', () => this.updateCollapseState(false));
    }
  }

  override loadData(): void {
    this.getAppointmentList(this.currentPage, this.pageSize);
  }

  getAppointmentList(
    page: number,
    size: number,
    filters: any = this.filterForm
  ): void {
    this.isLoading = true;
    this.appointmentsService
      .getListAppointment(page - 1, size, filters)
      .subscribe({
        next: (res) => {
          const data = res.data;
          if (data) {
            this.appointments = data.items;
            this.appointmentCards = this.appointments.map((a) =>
              this.mapToListCardItem(a)
            );

            if (this.filterForm.sortBy) {
              const isDesc = this.filterForm.sortDirection === 'desc';
              const multiplier = isDesc ? -1 : 1;

              this.appointmentCards.sort((a, b) => {
                let valA: any;
                let valB: any;

                if (this.filterForm.sortBy === 'createdDate') {
                  valA = new Date(a.createdDate).getTime();
                  valB = new Date(b.createdDate).getTime();
                } else if (this.filterForm.sortBy === 'patientName') {
                  valA = (a.name || '').toLowerCase();
                  valB = (b.name || '').toLowerCase();
                } else if (this.filterForm.sortBy === 'status') {
                  valA = a.appointmentStatus || 0;
                  valB = b.appointmentStatus || 0;
                } else if (this.filterForm.sortBy === 'createdBy') {
                  valA = (a.role || '').toLowerCase();
                  valB = (b.role || '').toLowerCase();
                } else {
                  return 0;
                }

                if (valA < valB) return -1 * multiplier;
                if (valA > valB) return 1 * multiplier;
                return 0;
              });
            }

            this.totalCount = data.count;
            this.totalPage = data.pages;
            this.currentPage = page;
          }
          this.isLoading = false;
        },

        error: (err) => {
          console.error('Error fetching appointments:', err);
          this.isLoading = false;
        },
      });
  }

  clearFilters(): void {
    this.filterForm = {
      patientName: '',
      bsnNumber: '',
      date: '',
      sortBy: null,
      sortDirection: null,
    };
    this.getAppointmentList(1, this.pageSize);
  }

  onSort(column: string) {
    if (this.filterForm.sortBy === column) {
      if (this.filterForm.sortDirection === 'asc') {
        this.filterForm.sortDirection = 'desc';
      } else {
        this.filterForm.sortBy = null;
        this.filterForm.sortDirection = null;
      }
    } else {
      this.filterForm.sortBy = column;
      this.filterForm.sortDirection = 'asc';
    }
    this.currentPage = 1;
    this.getAppointmentList(this.currentPage, this.pageSize);
  }

  private mapToListCardItem(a: Appointment): ListCardItem {
    return {
      id: a.id,
      name: a.patientName,
      email: '-',
      patientID: a.bsnNumber,
      age: 0,
      gender: '',
      phone: '-',
      location: '-',
      birthDate: '',
      createdDate: a.dateOfAppointment,
      role: a.createdBy,
      organization: '-',
      lastLoginDate: '',
      appointmentStatus: a.status,
    };
  }

  changePage(page: number) {
    if (page > 0 && page <= this.totalPage) {
      this.getAppointmentList(page, this.pageSize);
    }
  }

  changePageSize(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
    this.getAppointmentList(this.currentPage, this.pageSize);
  }

  onCardClick(card: ListCardItem) {
    if (!this.permissionService.hasPermission('create_wound_claim')) {
      return;
    }

    const appointment = this.appointments.find((a) => a.id === card.id);

    if (appointment?.patientId && appointment?.woundId) {
      // Navigate to follow-up wound creation page
      this.router.navigate(['/patient', 'edit', appointment.patientId], {
        queryParams: {
          startStep: 3,
          hideStepper: true,
          parentWoundId: appointment.woundId,
          woundType: 1, // 1 = Follow-up wound
        },
      });
    }
  }

  getColClass(col: ListColumnConfig): string {
    switch (col.colType) {
      case 'user':
        return 'col-user';
      case 'actions':
        return 'col-actions';
      default:
        return 'col-data';
    }
  }

  validateDateInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    if (value) {
      const parts = value.split('-');
      if (parts[0] && parts[0].length > 4) {
        parts[0] = parts[0].substring(0, 4);
        input.value = parts.join('-');
        this.filterForm.date = input.value;
      }
    }
  }
}
